import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { removeToast, Toast as ToastType } from '../store/slices/toastsSlice';

interface ToastProps {
  toast: ToastType;
}

const Toast: React.FC<ToastProps> = ({ toast }) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Set timeout to auto-dismiss
    const timer = setTimeout(() => {
      dispatch(removeToast(toast.id));
    }, toast.duration);

    // Cleanup timeout on unmount
    return () => {
      clearTimeout(timer);
    };
  }, [toast.id, toast.duration, dispatch]);

  const getBackgroundColor = (type: ToastType['type']): string => {
    switch (type) {
      case 'success':
        return '#4CAF50'; // Green
      case 'error':
        return '#f44336'; // Red
      case 'info':
        return '#2196F3'; // Blue
      case 'warning':
        return '#ff9800'; // Orange
      default:
        return '#2196F3';
    }
  };

  return (
    <div
      style={{
        backgroundColor: getBackgroundColor(toast.type),
        color: 'white',
        padding: '12px 20px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        marginBottom: '10px',
        minWidth: '250px',
        maxWidth: '400px',
        fontSize: '14px',
        fontWeight: '500',
        lineHeight: '1.5',
        wordWrap: 'break-word',
        animation: 'slideIn 0.3s ease-out'
      }}
    >
      {toast.message}
    </div>
  );
};

export const ToastContainer: React.FC = () => {
  const toasts = useAppSelector(state => state.toasts.toasts);

  return (
    <>
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
      <div
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end'
        }}
      >
        {toasts.map((toast: ToastType) => (
          <Toast key={toast.id} toast={toast} />
        ))}
      </div>
    </>
  );
};
